import styled, { css } from 'styled-components';

// STYLES
import { LoadingImg } from 'components/Loading/styles';
import colors, { opacity } from 'styles/colors';

// CONSTANTS
import { LOADING, DISABLED } from './commonClasses';
import { svgCSSColorOverride } from 'utils/colors';
import { PropsOf } from 'utils/types.utils';

export const BUTTON_BORDER_RADIUS = '2px';
export const BUTTON_HEIGHT = '32px';
export const MOBILE_BUTTON_HEIGHT = '40px';
export const BUTTON_PADDING = '7px 8px';

export enum BUTTON_INTENT {
  danger = 'danger',
  warning = 'warning',
  primary = 'primary',
  success = 'success',
}

const DisabledStylesMixin = css`
  color: ${colors.disabledButtonText};
  background-color: ${colors.disabledButtonBackground};
  border-color: ${colors.disabledButtonBackground};

  i {
    color: ${colors.disabledButtonText};
  }

  ${svgCSSColorOverride(colors.disabledButtonText)}
`;

const DisabledRule = css`
  &${`.${DISABLED}, .${DISABLED}:hover, .${DISABLED}:focus`}, &[disabled],
  &[disabled]:hover,
  &[disabled]:focus`;

const DisabledRulesMixin = css`
  ${DisabledRule} {
    ${DisabledStylesMixin};
    cursor: not-allowed;
    box-shadow: none;
    color: ${colors.disabledButtonText};
  }
`;

const SVGIconButtonMixin = css`
  svg {
    height: auto;
  }
`;

const ResponsiveButtonMixin = css`
  @media (max-width: 1080px) {
    .button-children {
      svg {
        margin-right: 0;
      }

      .button-text {
        display: none;
      }
    }
  }
`;

/**
 * Base button, it only controls the layout of the button itself, no the colors.
 * All types of color must be defined in new styled which are based on this one.
 */
export const LayoutButton = styled.button<{
  stretch?: boolean;
  mobile?: boolean;
  hasIcon?: boolean;
  loading?: boolean;
  iconOnly?: boolean;
}>`
  position: relative;

  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  ${({ iconOnly }) => !iconOnly && `min-width: 120px;`}

  width: ${({ stretch }) => (stretch ? '100%' : 'fit-content')};

  border-radius: ${BUTTON_BORDER_RADIUS};
  overflow: hidden;
  padding: 9px 16px;
  cursor: pointer;

  font-size: ${(props) => (props.mobile ? '16px' : '12px')};
  line-height: ${(props) => (props.mobile ? '16px' : '12px')};
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;

  ${(props) =>
    props.hasIcon &&
    `
    line-height: ${
      props.mobile ? '30px' : '24px'
    }; // when an Icon is present, its size is 24 and the text must adjust accordingly.
    padding: 3px 12px;
  `}

  transition-duration: 0.15s;
  transition-property: background-color, color, border-color;
  transition-timing-function: ease-in-out;

  .button-children {
    display: flex;
    flex-direction: row;
    align-items: center;

    & svg ~ div {
      margin-left: 4px;
      // ensure proper placement if children is a node and not just text
      display: flex;
      align-items: center;
    }
  }

  &${`.${LOADING}, .${LOADING}:hover, .${LOADING}:focus`} {
    ${LoadingImg} {
      border-top-color: ${colors.gray60};
      border-bottom-color: ${colors.gray60};
    }

    .button-children {
      visibility: hidden;
    }
  }

  & ${LoadingImg} {
    position: absolute;
    left: calc(50% - 0.5em);
    visibility: hidden;
  }

  ${(props) =>
    props.loading
      ? `
      ${LoadingImg} {
        visibility: visible;
      }

      .button-children {
        visibility: hidden;
      }
    `
      : null}

  ${DisabledRulesMixin};

  [class^='icon-'],
  [class*=' icon-'] {
    padding-left: 1em;
    line-height: calc(1em + 1px);
  }

  & + button {
    margin-left: 0.7em;
  }
`;

export const CommonButton = styled(LayoutButton)`
  background-color: ${colors.white};
  border: 1px solid ${colors.gray60};
  color: ${colors.primary};
  padding: ${BUTTON_PADDING};

  &:not([disabled]) {
    &:focus,
    &:hover {
      background-color: ${colors.white};
      color: ${colors.highlight};
      border-color: ${colors.highlight};

      i {
        color: ${colors.highlight};
      }
    }
  }

  i {
    color: ${colors.inactive};
  }
`;

export const PRIMARY_BUTTON_MIXIN = css`
  background-color: ${colors.primaryButton};
  color: ${colors.white};
  text-transform: uppercase;

  i {
    color: ${colors.white};
  }

  &:focus {
    box-shadow: 0 0 0 2px ${colors.highlightInactive};
  }

  &:not([disabled]) {
    &:hover {
      color: ${colors.white};
      background-color: ${colors.primaryButtonActive};

      i {
        color: ${colors.white};
      }
    }
  }
`;

export type StyledButtonProps = {
  mobile?: boolean;
  hasIcon?: boolean;
  intent?: BUTTON_INTENT;
  stretch?: boolean;
};

export const PrimaryButton = styled(LayoutButton)<StyledButtonProps>`
  flex-shrink: 0;

  background-color: ${({ intent = BUTTON_INTENT.primary }) => {
    switch (intent) {
      case BUTTON_INTENT.primary:
        return colors.primaryButton;
      case BUTTON_INTENT.danger:
        return colors.danger;
      case BUTTON_INTENT.success:
        return colors.success;
      case BUTTON_INTENT.warning:
        return colors.warning;
      default:
        return colors.primaryButton;
    }
  }};

  border: 1px solid rgb(0, 0, 0, 0);
  color: ${colors.white};
  text-transform: uppercase;

  &:not(${DisabledRule}) {
    &:hover {
      background-color: ${({ intent = BUTTON_INTENT.primary }) => {
        switch (intent) {
          case BUTTON_INTENT.primary:
            return colors.primaryButtonActive;
          case BUTTON_INTENT.danger:
            return colors.dangerActive;
          case BUTTON_INTENT.success:
            return colors.successActive;
          case BUTTON_INTENT.warning:
            return colors.warningActive;
          default:
            return colors.primaryButtonActive;
        }
      }};
    }
  }
`;

export const SecondaryButton = styled(LayoutButton)<StyledButtonProps>`
  flex-shrink: 0;
  color: ${colors.fleetBlue};
  background-color: ${colors.white};
  border: 1px solid ${colors.fleetOffGray};
  overflow: hidden;

  &:not([disabled]) {
    &:hover {
      border-color: ${colors.fleetOffGray};
      background-color: ${opacity(colors.lightBackground, 0.8)};
      mix-blend-mode: multiply;
    }

    &:active {
      background-color: ${colors.activeLightBackground};
    }
  }

  ${({ intent }) =>
    intent === BUTTON_INTENT.danger &&
    `
      color: ${colors.danger};
      border-color: ${colors.danger};

      ${svgCSSColorOverride(colors.danger)}

      &:not([disabled]) {
        &:hover {
          color: ${colors.white};
          background-color: ${colors.danger};
          border-color: ${colors.danger};


          ${svgCSSColorOverride(colors.white)}
        }

        &:active {
          color: ${colors.white};
          background-color: ${colors.redDarker};
          border-color: ${colors.red};
        }
      }
    `}
`;

export const ConfirmButton = styled(PrimaryButton)`
  color: ${colors.white};
  background-color: ${colors.success};

  &:not([disabled]) {
    &:hover,
    &:focus {
      background-color: ${colors.successActive};
    }
  }
`;

/**
 * @deprecated Use `ConfirmButton` instead.
 */
export const NewConfirmButton = ConfirmButton;

export const DangerButton = styled(PrimaryButton)`
  color: ${colors.white};
  background-color: ${colors.danger};

  &:not(${DisabledRule}) {
    &:hover,
    &:focus {
      background-color: ${colors.dangerActive};
    }
  }

  &:focus {
    box-shadow: 0 0 0 2px ${colors.dangerFocus};
  }
`;

export const LightDangerButton = styled(SecondaryButton)`
  color: ${colors.red};
  background-color: ${colors.white};
  border: 1px solid ${colors.red};

  &:not([disabled]) {
    &:hover,
    &:focus {
      color: ${colors.white};
      background-color: ${colors.red};
      border: 1px solid ${colors.red};
    }
  }

  &:focus {
    box-shadow: 0 0 0 2px ${colors.dangerFocus};
  }
`;

export const SuccessButton = (props: Omit<PropsOf<typeof PrimaryButton>, 'intent'>) => (
  <PrimaryButton {...props} intent={BUTTON_INTENT.success} />
);

export const TableButtonWrapper = styled.div`
  padding-top: 10px;
`;

export const LightTableButton = styled(SecondaryButton)`
  min-width: 100px;
  text-transform: uppercase;
`;

export const PrimaryTableButton = styled(PrimaryButton)`
  min-width: 100px;
  text-transform: uppercase;
`;

export const DisabledButton = styled(PrimaryButton)`
  ${ResponsiveButtonMixin};
  ${DisabledStylesMixin};

  &:hover,
  &:focus {
    ${DisabledStylesMixin};
  }

  &:not([disabled]) {
    &:hover,
    &:focus {
      ${DisabledStylesMixin};
    }
  }

  ${SVGIconButtonMixin};
`;

export const FormButtonStyle = styled(CommonButton)`
  background-color: ${colors.formButton};
  border-color: ${colors.inactive};
  border-radius: 2px;
  box-shadow: 0 2px 4px 0 ${colors.gray85};
  color: ${colors.black};
  font-size: ${(props) => (props.mobile ? '17px' : '13px')};
  height: ${(props) => (props.mobile ? MOBILE_BUTTON_HEIGHT : BUTTON_HEIGHT)};
  line-height: 13px;
`;

export const FlatButtonStyle = styled(LayoutButton)`
  display: inline;
  min-width: auto !important;
  height: ${(props) => (props.mobile ? MOBILE_BUTTON_HEIGHT : BUTTON_HEIGHT)};
  padding: 0 12px;
  border: 0;
  line-height: initial;
  font-size: ${(props) => (props.mobile ? '16px' : '12px')};
  color: ${colors.primaryButton};

  &:hover {
    background-color: ${opacity(colors.fleetOffGray, 0.4)};
  }

  &[disabled] {
    ${DisabledStylesMixin};
  }

  ${ResponsiveButtonMixin};
  ${DisabledRulesMixin};
  ${SVGIconButtonMixin};
`;

export const LinkButton = styled.button`
  border: 0;
  color: ${colors.highlight};
  display: inline;
  font-size: 13px;
  text-decoration: underline;

  text &:hover:not([disabled]),
  &:focus:not([disabled]) {
    color: ${colors.highlightActive};
  }

  ${DisabledRulesMixin};
`;

export const ButtonList = styled.span`
  display: inline-flex;
  align-items: center;

  button {
    margin-left: 17px;

    &:first-child {
      margin-left: 0;
    }
  }
`;

export const IconButtonStyle = styled(LinkButton)`
  background-color: transparent;
  border: 0;
  font-size: 1.4em;
  height: 1em;
  padding: 0;
  text-decoration: none;
  width: 1em;

  color: ${colors.gray60};

  &:not([disabled]) {
    &:hover {
      color: ${colors.primaryButton};
    }

    &:focus {
      color: ${colors.gray60};
    }
  }
`;

export const SVGButton = styled.button<{ size?: number }>`
  border: none;
  padding: ${({ size = 32 }) => Math.max(size - 24, 0) / 2}px;
  background-color: ${opacity(colors.white, 0)};
  border-radius: 3px;
  transition: background-color 200ms;

  &:hover {
    background-color: ${colors.lightBackground};
  }
  ${({ color = colors.fleetBlack }) => svgCSSColorOverride(color)}

  &:disabled {
    cursor: default;
    &:hover {
      background-color: inherit;
    }

    ${({ color = colors.fleetBlack }) => svgCSSColorOverride(opacity(color, 0.4))}
  }

  ${DisabledRulesMixin};
`;

export const ButtonContext = styled.span`
  font-size: 12px;
  color: ${colors.gray55};
  font-weight: 400;
  margin-left: 15px;
`;

export const StyledAddButton = styled(SecondaryButton)`
  div {
    display: flex;
    align-items: center;
  }

  svg {
    margin-left: -6px;
  }
`;

export const StyledRemoveButton = styled(StyledAddButton)`
  color: ${colors.removeButton};

  svg {
    margin-right: 3px;
  }
`;
